import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BrokerPortalFeatureCodes, UserClass } from '@brokerportal/common/enums';
import { PortalConfiguration } from '@brokerportal/common/models/portal-configuration.model';
import { FeatureFlagService } from '@brokerportal/common/services/feature-flag.service';
import { EMPTY, interval, Subject, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { setRumResourceTracking } from './rum-resource-filter';
import { PartnerContext, TelemetryService } from './telemetry.service';

/** Re-read the flags this often, so turning one off reaches tabs left open. */
const RUM_FLAG_REFRESH_MS = 5 * 60 * 1000;

/**
 * Each new partner (or none) cancels the flag check still pending for the previous one, so a late flag result
 * can't tag or clear the wrong partner.
 */
@Injectable({ providedIn: 'root' })
export class RumContextService implements OnDestroy {
    private readonly partner$ = new Subject<PartnerContext | undefined>();
    private readonly refresh$ = new Subject<void>();
    private readonly subscriptions = new Subscription();

    constructor(
        private featureFlagService: FeatureFlagService,
        private telemetry: TelemetryService,
        private router: Router,
        private ngZone: NgZone
    ) {}

    start(): void {
        this.subscriptions.add(
            this.refresh$
                .pipe(
                    switchMap(() =>
                        this.featureFlagService.isEnabled(BrokerPortalFeatureCodes.EnableRumResourceTracking)
                    )
                )
                .subscribe(setRumResourceTracking)
        );
        this.subscriptions.add(
            this.partner$
                .pipe(
                    tap(context => {
                        // Context left from an earlier page load, for another partner or class, is cleared at once.
                        if (!isSameContext(this.telemetry.getPartnerContext(), context)) {
                            this.telemetry.clearPartnerContext();
                        }
                    }),
                    switchMap(context =>
                        context
                            ? this.refresh$.pipe(
                                  startWith(undefined),
                                  switchMap(() =>
                                      this.featureFlagService.isEnabled(BrokerPortalFeatureCodes.EnableRumUserContext)
                                  ),
                                  map(enabled => (enabled ? context : undefined))
                              )
                            : EMPTY
                    )
                )
                .subscribe(context =>
                    context ? this.telemetry.setPartnerContext(context) : this.telemetry.clearPartnerContext()
                )
        );

        this.subscriptions.add(
            this.router.events
                .pipe(filter(event => event instanceof NavigationEnd))
                .subscribe(() => this.refresh$.next())
        );
        this.ngZone.runOutsideAngular(() =>
            this.subscriptions.add(
                interval(RUM_FLAG_REFRESH_MS)
                    .pipe(filter(() => !document.hidden))
                    .subscribe(() => this.refresh$.next())
            )
        );
    }

    setPartner(config: PortalConfiguration | undefined): void {
        this.partner$.next(partnerContext(config));
    }

    /** Sign-out, or a sign-in page that still holds context. Every path that ends a user's session calls this. */
    endUserSession(): void {
        this.telemetry.endUserSession();
        this.partner$.next(undefined);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}

function partnerContext(config: PortalConfiguration | undefined): PartnerContext | undefined {
    if (!config?.partner) {
        return undefined;
    }
    const userClass = userClassName(config.user?.userClass);
    const partnerId = String(config.partner.id);
    return userClass === undefined ? { partnerId } : { partnerId, userClass };
}

// PortalConfigurationService maps the API value with a two-way enum lookup, so the config holds a name or a number.
function userClassName(value: UserClass | string | undefined | null): string | undefined {
    return typeof value === 'number' ? UserClass[value] : value ?? undefined;
}

function isSameContext(held: PartnerContext | undefined, next: PartnerContext | undefined): boolean {
    return held?.partnerId === next?.partnerId && held?.userClass === next?.userClass;
}
